import React, {useEffect} from 'react';
import {Button, Spinner} from '@goproblems/ui';
import {SingleProblem, NextProblemEntryEnum} from '@types';
import {useHotkeys} from 'react-hotkeys-hook';
import {useGetActiveSrsSessionQuery, useGetReadyTrainingProblemsQuery} from '@api';
import {
    useDispatch,
    useSelector,
    getShortLevelName,
    getShortLevelNameFromRank,
    useProblemFilterQueryParams,
} from '@utils';
import {uiSlice} from '@reducers';
import {useTranslation} from 'react-i18next';
import {t} from 'i18next';
import {upperFirst} from 'lodash';

interface NextTrainingBtnTextProps {
    currentProblemId: number;
}

const NextTrainingBtnText = ({
    currentProblemId,
}: NextTrainingBtnTextProps) => {
    const trainingProblemIds = useSelector(
        state => state.ui.trainingProblemIds
    );
    const {
        data: activeSession,
        error: activeError,
        isLoading: activeLoading,
        refetch: refetchActive,
    } = useGetActiveSrsSessionQuery(undefined, {
        skip: trainingProblemIds !== null,
        refetchOnMountOrArgChange: true,
    });
    const {data, error, isLoading, refetch} =
        useGetReadyTrainingProblemsQuery(undefined, {
            skip:
                trainingProblemIds !== null ||
                activeLoading ||
                activeSession !== null,
            refetchOnMountOrArgChange: true,
        });
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const sessionProblemIds =
        trainingProblemIds ??
        activeSession?.remainingProblemIds ??
        data?.problemIds ??
        null;
    const remainingProblemCount =
        sessionProblemIds?.filter(id => id !== currentProblemId).length ??
        null;

    useEffect(() => {
        if (activeSession) {
            dispatch(
                uiSlice.actions.restoreTrainingSession(
                    activeSession.remainingProblemIds
                )
            );
        }
    }, [activeSession, dispatch]);

    if (error || activeError) {
        return (
            <div
                className="card bg-dark text-white"
                style={{minWidth: '400px'}}>
                <div className="alert alert-danger" role="alert">
                    {t('training.error_loading_data')}
                    <button
                        className="btn btn-outline-danger btn-sm ms-2"
                        onClick={() => {
                            if (activeError) {
                                void refetchActive();
                            } else {
                                void refetch();
                            }
                        }}>
                        {t('training.retry')}
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading || activeLoading || remainingProblemCount === null) {
        return (
            <>
                {t('training.title')} <Spinner size={'sm'} />
            </>
        );
    }

    if (remainingProblemCount <= 0) {
        return <span>{t('training.finish')}</span>;
    }

    return (
        <span>
            {t('training.next_with_count', {
                count: remainingProblemCount,
            })}
        </span>
    );
};

type NextProblemDisplayNameProps = {
    problem: SingleProblem;
};

const NextProblemDisplayName: React.FC<NextProblemDisplayNameProps> = ({
    problem,
}) => {
    const argument = useProblemFilterQueryParams();
    let displayName: React.ReactNode = '';

    switch (argument.name) {
        case NextProblemEntryEnum.RECOMMENDED: {
            if (!problem) return null;
            displayName = getShortLevelNameFromRank(problem.rank);
            break;
        }
        case NextProblemEntryEnum.LEVEL_PROGRESS: {
            if (problem) {
                displayName = getShortLevelNameFromRank(problem.rank);
            } else if (
                argument.rating !== null &&
                argument.rating !== undefined
            ) {
                displayName = getShortLevelName(argument.rating);
            }
            break;
        }
        case NextProblemEntryEnum.SPEED: {
            displayName = t('single_problem.next_types.speed');
            if (
                argument.levelFrom !== null &&
                argument.levelFrom !== undefined
            ) {
                displayName = t('single_problem.next_types.speed_level', {
                    level: getShortLevelName(argument.levelFrom),
                });
            }
            break;
        }
        case NextProblemEntryEnum.COLLECTION: {
            if (!problem) return null;
            const collection = problem.collections.find(
                c => c.id === argument.collectionId
            );
            if (collection) {
                displayName = collection.name;
            }
            break;
        }
        case NextProblemEntryEnum.MADE_BY_USER: {
            displayName = t('single_problem.next_types.by_user', {
                name: argument.madeByUser,
            });
            break;
        }
        case NextProblemEntryEnum.FILTER:
            displayName = t('single_problem.next_types.filter');
            break;
        case NextProblemEntryEnum.AUTOLEVEL:
            displayName = t(`single_problem.next_types.${argument.name}`);
            break;
        case NextProblemEntryEnum.TRAINING:
            return (
                <NextTrainingBtnText
                    currentProblemId={problem.id}
                />
            );
        case NextProblemEntryEnum.BEGINNER:
        case NextProblemEntryEnum.EASY:
        case NextProblemEntryEnum.MEDIUM:
        case NextProblemEntryEnum.HARD: {
            displayName = upperFirst(
                t(`single_problem.next_types.${argument.name}`)
            );
            break;
        }
        case NextProblemEntryEnum.NEEDING_RATING: {
            displayName = t('single_problem.next_types.needing_rating');
            break;
        }
        case NextProblemEntryEnum.NEWEST: {
            displayName = t('single_problem.next_types.newest');
            break;
        }
        case NextProblemEntryEnum.FAVORITES: {
            displayName = t('single_problem.next_types.favorites');
            break;
        }
        case NextProblemEntryEnum.PRACTICE: {
            displayName = t('single_problem.next_types.practice');
            break;
        }
    }

    return (
        <>
            {t('single_problem.next')} {displayName}
        </>
    );
};

interface NextBtnProps {
    problem: SingleProblem;
    disabled: boolean;
    onClick: () => Promise<void>;
}

const NextBtn: React.FC<NextBtnProps> = ({problem, disabled, onClick}) => {
    useHotkeys(
        ['Option+n', 'Alt+n'],
        () => {
            void onClick();
        },
        {preventDefault: true}
    );
    return (
        <Button
            variant="success"
            className="btn relative min-w-[60px] !rounded"
            disabled={disabled}
            onClick={() => {
                void onClick();
            }}>
            <span
                className={disabled ? 'invisible' : undefined}
                aria-hidden={disabled || undefined}>
                <NextProblemDisplayName problem={problem} />
            </span>
            {disabled && (
                <span className="absolute inset-0 flex items-center justify-center">
                    <Spinner size="sm" />
                </span>
            )}
        </Button>
    );
};

export default NextBtn;
